import React from "react";
import { Link } from "react-router-dom";
import { Clock, Eye, Calendar, User as UserIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BlogPost } from "../../../global-components/src/types";
import { getImageUser, getImageUserNotFound } from "../../../global-components/src/utils/auth";
import { BUCKET_PACOCA_URL } from "../../../global-components/src/utils/vars";
import ImgVerified from "../../../global-components/src/components/img-verified";
import "./blog-card.css";

const coverSrc = (v: string) => v.startsWith("http") ? v : `${BUCKET_PACOCA_URL}/${v}`;

interface BlogCardProps {
    post: BlogPost;
    featured?: boolean;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, featured = false }) => {
    const { t, i18n } = useTranslation();
    const dateSource = post.published_at ? new Date(post.published_at) : new Date(post.created_at);
    const localeCode = i18n.language === "us" ? "en-US" : (i18n.language === "es" ? "es-ES" : "pt-BR");
    const formattedDate = dateSource.toLocaleDateString("pt-BR", {
        day: "numeric",
        month: "long",
        year: "numeric"
    });

    return (
        <article className={`blog-card ${featured ? "blog-card-featured" : ""}`}>
            {post.cover_image && (
                <Link to={`/blog/artigo/${post.slug}`} className="blog-card-img-link">
                    <img
                        src={coverSrc(post.cover_image!)}
                        alt={post.title}
                        className="blog-card-img"
                        loading="lazy"
                    />
                </Link>
            )}

            <div className="blog-card-content">
                <div className="blog-card-meta">
                    <span className="blog-card-badge">{t("blog.communityBadge")}</span>
                    <span className="blog-card-dot">•</span>
                    <span className="blog-card-readtime">
                        <Clock size={13} />
                        {t("blog.readTime", { count: post.read_time })}
                    </span>
                    {post.views_count > 0 && (
                        <>
                            <span className="blog-card-dot">•</span>
                            <span className="blog-card-views">
                                <Eye size={13} />
                                {t("blog.viewsCount", { count: post.views_count })}
                            </span>
                        </>
                    )}
                </div>

                <h3 className="blog-card-title">
                    <Link to={`/blog/artigo/${post.slug}`}>{post.title}</Link>
                </h3>

                {post.summary && <p className="blog-card-summary">{post.summary}</p>}

                <div className="blog-card-footer">
                    {post.user ? (
                        <Link to={`/@${post.user.user_name}`} className="blog-card-author">
                            <img
                                src={getImageUser(post.user.img_account, undefined, post.user.name)}
                                onError={(e) => { e.currentTarget.src = getImageUserNotFound(post.user?.name); }}
                                alt={post.user.name}
                                className="blog-card-avatar"
                            />
                            <div className="blog-card-author-info">
                                <span className="blog-card-author-name">
                                    {post.user.name}
                                    <ImgVerified user={post.user} classNameProp="blog-verified-badge" />
                                </span>
                                <span className="blog-card-date">
                                    <Calendar size={11} />
                                    {formattedDate}
                                </span>
                            </div>
                        </Link>
                    ) : (
                        <div className="blog-card-author">
                            <div className="blog-card-author-info">
                                <span className="blog-card-date">{formattedDate}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
};

export default BlogCard;
